/**
 * Column geometry for DataTable's priority / auto-hide system.
 *
 * Pure functions over a column's declared width contract, so the fit maths can
 * be reasoned about (and tested) without a DOM. See
 * docs/design-system/ui-list-pages.md §4.1.
 *
 * ## The width contract
 *
 * | Declaration            | Kind        | Renders at              | Floor      |
 * |------------------------|-------------|-------------------------|------------|
 * | `width: 140`           | fixed       | exactly 140px           | 140        |
 * | `width: 150, minWidth: 110` | shrinkable | 150px, shrinking to 110 | 110    |
 * | `flex: 2, minWidth: 220`    | flex    | share of leftover space | 220        |
 * | `width: '50%'`         | passthrough | the literal string      | (excluded) |
 *
 * A column's **floor** is the narrowest it may render. Auto-hide compares the
 * sum of floors — not preferred widths — against the container, so a table
 * shrinks its columns before it drops any. Declaring `minWidth` alongside
 * `width` is the opt-in: without it, `width` is a hard floor and the column is
 * dropped rather than squeezed.
 */

/** Default floor for a flex column that declares no `minWidth`. */
export const MIN_FLEX_WIDTH_PX = 80;

/** Priority tiers: 1 = essential (never auto-dropped) … 4 = optional (dropped first). */
export type ColumnLayoutSpec = {
  id: string;
  width?: number | string;
  minWidth?: number;
  flex?: number;
  priority?: number;
};

/** A numeric width, or a `"140px"` string. Anything else (`"50%"`) is not a number. */
function widthAsNumber(w: number | string | undefined): number | null {
  if (typeof w === 'number') return w;
  if (typeof w === 'string') {
    const n = parseFloat(w);
    if (!Number.isNaN(n) && /px$/.test(w.trim())) return n;
  }
  return null;
}

/** True when the column takes a share of leftover space rather than a fixed width. */
function isFlex(c: ColumnLayoutSpec): boolean {
  return c.flex !== undefined || c.width === undefined;
}

/** True when the column renders a literal string width we neither size nor measure. */
function isPassthrough(c: ColumnLayoutSpec): boolean {
  return !isFlex(c) && widthAsNumber(c.width) === null;
}

/**
 * The narrowest this column may render.
 *
 * `minWidth` wins over `width` — that is the whole point of co-declaring them.
 * A bare numeric `width` is its own floor (the column cannot shrink).
 */
export function columnFloor(c: ColumnLayoutSpec): number {
  if (c.minWidth !== undefined) return c.minWidth;
  const w = widthAsNumber(c.width);
  if (w !== null) return w;
  return MIN_FLEX_WIDTH_PX;
}

/** The width the column would like, when there is room for it. */
function columnBasis(c: ColumnLayoutSpec): number {
  return widthAsNumber(c.width) ?? columnFloor(c);
}

/** How much the column can give up before hitting its floor. */
function shrinkCapacity(c: ColumnLayoutSpec): number {
  return Math.max(0, columnBasis(c) - columnFloor(c));
}

/**
 * Sum of every column's floor.
 *
 * Passthrough (`'50%'`) columns are counted at `minWidth ?? MIN_FLEX_WIDTH_PX` —
 * we can't measure a percentage, but excluding them would let the fit check
 * conclude a table fits while a real column still occupies space, and would let
 * the drop loop hide a passthrough column for zero fit benefit.
 */
function floorSum(columns: ColumnLayoutSpec[]): number {
  return columns.reduce((sum, c) => sum + columnFloor(c), 0);
}

/**
 * Columns to auto-hide so the rest fit at their floors.
 *
 * Repeatedly drops the highest priority *number* (4 before 3 before 2). Priority
 * 1 is never dropped; when only priority-1 columns remain and they still don't
 * fit, the caller falls through to horizontal scroll.
 */
export function computeAutoHiddenColumns(
  columns: ColumnLayoutSpec[],
  availableWidth: number
): string[] {
  if (availableWidth <= 0) return [];

  const visible = [...columns];
  const hidden: string[] = [];
  let total = floorSum(visible);

  while (total > availableWidth) {
    let dropIdx = -1;
    let dropPriority = 1;
    for (let i = 0; i < visible.length; i++) {
      const p = visible[i].priority ?? 1;
      if (p > dropPriority) {
        dropPriority = p;
        dropIdx = i;
      }
    }
    if (dropIdx === -1) break; // only priority-1 columns left → horizontal scroll
    const [removed] = visible.splice(dropIdx, 1);
    hidden.push(removed.id);
    total = floorSum(visible);
  }

  return hidden;
}

/**
 * True when the visible columns overflow the container even at their floors —
 * the only case that warrants horizontal scroll. Preferred widths exceeding the
 * container is normal: shrinkable columns absorb it.
 */
export function hasFloorOverflow(
  columns: ColumnLayoutSpec[],
  availableWidth: number
): boolean {
  if (availableWidth <= 0) return false;
  return floorSum(columns) > availableWidth;
}

/**
 * Final rendered width per column id, for `tableLayout: 'fixed'`.
 *
 * Non-flex columns take their basis; if that plus the flex columns' floors
 * overruns the container, the deficit is reclaimed from shrinkable columns
 * **least-important-first** — the whole priority-4 tier is drained to its floors
 * before priority 3 gives up a pixel, and so on. That keeps priority-1 identity
 * columns (Reference, Name, Subject) at their preferred width for as long as any
 * optional column still has slack, instead of wrapping them to two lines while a
 * "Created" column sits at full width. Within a tier the give is proportional to
 * each column's spare capacity. No column ever goes below its floor.
 *
 * Whatever remains is distributed to flex columns by weight.
 *
 * Passthrough (`'50%'`) columns pass through untouched and take no part in the
 * allocation — we can't do arithmetic on a percentage.
 */
export function computeColumnWidths(
  columns: ColumnLayoutSpec[],
  availableWidth: number
): Record<string, number | string | undefined> {
  const widths: Record<string, number | string | undefined> = {};

  const flexCols: ColumnLayoutSpec[] = [];
  const nonFlexCols: ColumnLayoutSpec[] = [];
  for (const c of columns) {
    if (isPassthrough(c)) {
      widths[c.id] = c.width;
    } else if (isFlex(c)) {
      flexCols.push(c);
    } else {
      nonFlexCols.push(c);
    }
  }

  const nonFlexBasis = nonFlexCols.reduce((s, c) => s + columnBasis(c), 0);
  const flexFloors = flexCols.reduce((s, c) => s + columnFloor(c), 0);
  const deficit = nonFlexBasis + flexFloors - availableWidth;

  // Drain tiers from the least important (highest priority number) down.
  const give = new Map<string, number>();
  let outstanding = Math.max(0, deficit);
  const tiers = [...new Set(nonFlexCols.map((c) => c.priority ?? 1))].sort((a, b) => b - a);
  for (const tier of tiers) {
    if (outstanding <= 0) break;
    const inTier = nonFlexCols.filter((c) => (c.priority ?? 1) === tier);
    const tierCapacity = inTier.reduce((s, c) => s + shrinkCapacity(c), 0);
    if (tierCapacity <= 0) continue;
    const take = Math.min(outstanding, tierCapacity);
    for (const c of inTier) {
      give.set(c.id, (take * shrinkCapacity(c)) / tierCapacity);
    }
    outstanding -= take;
  }

  let nonFlexTotal = 0;
  for (const c of nonFlexCols) {
    const w = Math.max(columnFloor(c), Math.round(columnBasis(c) - (give.get(c.id) ?? 0)));
    widths[c.id] = w;
    nonFlexTotal += w;
  }

  if (flexCols.length === 0) return widths;

  // Water-fill the flex columns. A naive `max(floor, share)` silently overflows:
  // boosting an under-floor column up to its floor takes nothing back from the
  // others, so the total can exceed the budget even though every floor fits.
  // Instead, pin each column whose weighted share falls below its floor, subtract
  // it from the budget, and redistribute what remains among the rest until stable.
  let pool = [...flexCols];
  let budget = Math.max(0, availableWidth - nonFlexTotal);

  for (;;) {
    if (pool.length === 0) break;

    const flexSum = pool.reduce((s, c) => s + (c.flex ?? 1), 0);
    if (budget <= 0 || flexSum <= 0) {
      // Nothing left to share (or the floors already overflow — hasFloorOverflow
      // reports that separately and the table scrolls).
      for (const c of pool) widths[c.id] = columnFloor(c);
      break;
    }

    const unit = budget / flexSum;
    const underFloor = pool.filter((c) => (c.flex ?? 1) * unit < columnFloor(c));

    if (underFloor.length === 0) {
      // Floor is used as a `max` only to absorb the rounding-down of the share.
      for (const c of pool) widths[c.id] = Math.max(columnFloor(c), Math.floor((c.flex ?? 1) * unit));
      break;
    }

    for (const c of underFloor) {
      widths[c.id] = columnFloor(c);
      budget -= columnFloor(c);
    }
    pool = pool.filter((c) => !underFloor.includes(c));
  }

  return widths;
}
